import { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowUturnLeftIcon, PaperAirplaneIcon } from "@heroicons/react/24/outline";
import { toast } from "../../../utils/toast";
import { hasAnyRole } from "../../../utils/auth";
import { ROLES } from "../../../auth/roles";
import { Button, InfoBox, Loading, Modal, Textarea } from "../../ui";
import { errorMessageOf, newIdempotencyKey, scholarshipsApi } from "../../../api/scholarships";
import { SCHOLARSHIPS_KEY, staffScholarshipKey } from "../model";
import { REVIEW_KEY, awardListKey, formatDateTime } from "../review/model";
import { TransmissionDialog, nomineesGo, transmissionKey } from "../cooperation/Transmission";
import { LOANS_KEY } from "./model";

const MAX_REASON = 1000;

/**
 * A sponsored scholarship's nomination list: the DG previews what the sponsor receives (transmission
 * letter and award list) and approves and sends it, or returns it with a reason. A list the DG made
 * went to the sponsor at once.
 */
export default function SignOffPanel({ scholarship }) {
  const queryClient = useQueryClient();
  const isDg = hasAnyRole(ROLES.DG);
  const [confirming, setConfirming] = useState(false);
  const [viewing, setViewing] = useState(false);
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState("");
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  // One key per sign-off: a retry after a lost answer reuses it, the next sign-off gets a new one
  const [signOffKey, setSignOffKey] = useState(newIdempotencyKey);

  const { data, isPending } = useQuery({
    queryKey: awardListKey(scholarship.id),
    queryFn: () => scholarshipsApi.get(`admin/scholarships/${scholarship.id}/award-list`),
  });
  const list = data?.data;

  if (isPending) return <Loading label="Loading the nomination list…" />;
  if (!list) return null;

  const done = (result, message) => {
    queryClient.setQueryData(awardListKey(scholarship.id), result);
    queryClient.invalidateQueries({ queryKey: LOANS_KEY });
    queryClient.invalidateQueries({ queryKey: REVIEW_KEY });
    queryClient.invalidateQueries({ queryKey: transmissionKey(scholarship.id) });
    queryClient.invalidateQueries({ queryKey: staffScholarshipKey(scholarship.id) });
    queryClient.invalidateQueries({ queryKey: [...SCHOLARSHIPS_KEY, "staff", "list"] });
    toast.success(message);
  };

  const signOff = async (note) => {
    setBusy(true);
    try {
      const result = await scholarshipsApi.post(`staff/award-lists/${list.id}/sign-off`, note ? { note } : {}, { ifMatch: data.etag, idempotencyKey: signOffKey });
      done(result, `Approved and sent to the partner under ${result.data?.transmissionReference ?? "a new reference"}.`);
      setSignOffKey(newIdempotencyKey());
      setConfirming(false);
    } catch (err) {
      console.error(`Failed to sign off the nomination list ${list.id}:`, err);
      toast.error(errorMessageOf(err, "Failed to sign off the nomination list. Please reload and try again."));
    } finally {
      setBusy(false);
    }
  };

  const sendBack = async () => {
    setError(null);
    if (!reason.trim()) return setError("Say why the list goes back to the Scholarship Admin.");
    setBusy(true);
    try {
      const result = await scholarshipsApi.post(`staff/award-lists/${list.id}/return`, { reason: reason.trim() }, { ifMatch: data.etag });
      done(result, "Nomination list returned to the Scholarship Admin");
      setReturning(false);
      setReason("");
    } catch (err) {
      console.error(`Failed to return the nomination list ${list.id}:`, err);
      setError(errorMessageOf(err, "Failed to return the nomination list. Please reload and try again."));
    } finally {
      setBusy(false);
    }
  };

  if (list.signedOffAt) {
    return (
      <InfoBox>
        Sent to the partner by {list.signedOffBy?.name ?? "the DG"} {formatDateTime(list.signedOffAt)}
        {list.transmissionReference && ` under ${list.transmissionReference}`}. The nominees are with the partner, who marks each one granted or not granted.{" "}
        <button type="button" className="font-medium text-brand hover:underline" onClick={() => setViewing(true)}>
          View what was sent
        </button>
        <TransmissionDialog scholarship={scholarship} open={viewing} onClose={() => setViewing(false)} />
      </InfoBox>
    );
  }
  if (list.state !== "FINALISED") {
    return <InfoBox>Returned to the Scholarship Admin {formatDateTime(list.returnedAt)}: “{list.returnReason}”</InfoBox>;
  }

  const counts = list.counts ?? {};
  return (
    <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-white p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="text-sm text-slate-700">
        <p className="font-semibold text-slate-800">{isDg ? "Your sign-off is needed" : "Waiting for the DG's sign-off"}</p>
        <p>
          Sent by {list.finalisedBy?.name ?? "the Scholarship Admin"} {formatDateTime(list.finalisedAt)}: {counts.AWARDED ?? 0} nominated, {counts.RESERVE ?? 0} reserve, {counts.NOT_AWARDED ?? 0} not nominated.
          {list.returnReason && ` Returned once before: “${list.returnReason}”.`}
        </p>
      </div>
      {isDg && (
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" icon={<ArrowUturnLeftIcon className="h-4 w-4" />} onClick={() => setReturning(true)} disabled={busy}>
            Return to the Scholarship Admin
          </Button>
          <Button icon={<PaperAirplaneIcon className="h-4 w-4" />} onClick={() => setConfirming(true)} disabled={busy}>
            Preview and send
          </Button>
        </div>
      )}
      <TransmissionDialog
        scholarship={scholarship}
        open={confirming}
        onClose={() => setConfirming(false)}
        onSend={signOff}
        busy={busy}
        description={`${nomineesGo(counts.AWARDED)} to the partner with this letter and their documents; the partner's members are told. This cannot be undone.`}
      />
      <Modal
        open={returning}
        onClose={() => setReturning(false)}
        size="md"
        dismissible={!busy}
        title="Return the nomination list?"
        description="The Scholarship Admin gets the list back as a draft, with your reason, and may send it again. Students see their application as under evaluation again."
        footer={
          <>
            <Button variant="ghost" size="sm" onClick={() => setReturning(false)} disabled={busy}>
              Cancel
            </Button>
            <Button size="sm" variant="danger" onClick={sendBack} loading={busy}>
              Return
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-2">
          <Textarea label="Reason" required rows={3} maxLength={MAX_REASON} value={reason} onChange={(e) => setReason(e.target.value)} />
          {error && <p className="text-sm text-red-600">{error}</p>}
        </div>
      </Modal>
    </div>
  );
}
